import { Button } from "@/shared/ui/button";
import type { BriefingPayload } from "@/entities/genesis";

interface Props {
  briefing: BriefingPayload;
  onEnter: () => Promise<void> | void;
}

const KIND_LABEL: Record<string, string> = {
  brand_brain: "Brand Brain",
  business_brain: "Business Brain",
  campaigns: "Initial Campaign Roadmap",
  assets: "First Marketing Assets",
  calendar_entries: "Content Calendar",
  knowledge_entries: "Knowledge Brain",
};

export function BriefingStep({ briefing, onEnter }: Props): JSX.Element {
  const ready = briefing.created_deliverables.filter((item) => item.count > 0);
  return (
    <div className="mx-auto max-w-3xl space-y-10 pb-16" data-testid="briefing-step">
      <div className="space-y-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#c7a25a]">
          Executive briefing
        </p>
        <h2 className="font-serif text-4xl leading-[1.1] text-[#f5efe6] md:text-6xl" style={{ fontFamily: "'Fraunces', serif" }}>
          Welcome to <span className="italic text-[#c7a25a]">{briefing.business_name}</span>.
        </h2>
        <p className="max-w-xl text-[15px] leading-relaxed text-[#f5efe6]/80 md:text-[16px]">
          Your workspace is ready. Here is what Genesis prepared from your answers.
        </p>
      </div>

      <div className="rounded-3xl border border-[#f5efe6]/8 bg-[#0f1613]/70 p-6 md:p-8">
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#c7a25a]">
          Ready in your workspace
        </p>
        <ul className="grid gap-3 md:grid-cols-2" data-testid="briefing-deliverables">
          {ready.map((d) => (
            <li key={d.kind} className="flex items-center gap-3 rounded-xl border border-[#f5efe6]/6 px-4 py-3">
              <span className="grid h-6 w-6 place-items-center rounded-full bg-[#c7a25a] text-[11px] text-[#0d1310]">✓</span>
              <span className="text-[14px] text-[#f5efe6]">
                {KIND_LABEL[d.kind] ?? d.kind}
              </span>
              <span className="ml-auto font-mono text-[11px] tracking-wider text-[#c7a25a]">
                {d.count > 1 ? `× ${d.count}` : ""}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {briefing.observations.length > 0 && (
        <div className="space-y-3" data-testid="briefing-observations">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c7a25a]">
            I noticed…
          </p>
          {briefing.observations.map((o) => (
            <div key={o.id} className="rounded-3xl border border-[#f5efe6]/8 bg-[#0f1613]/70 p-5">
              <p className="text-[15px] font-medium text-[#f5efe6]">{o.title}</p>
              <p className="mt-2 text-[14px] leading-relaxed text-[#f5efe6]/70">{o.body}</p>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c7a25a]">
          What would you like us to work on first?
        </p>
        <div className="flex flex-wrap gap-2" data-testid="briefing-next-actions">
          {briefing.next_recommended_actions.map((a) => (
            <span
              key={a.key}
              className="rounded-full border border-[#f5efe6]/12 bg-[#0f1613] px-4 py-2 text-[13px] text-[#f5efe6]/80"
            >
              {a.label}
            </span>
          ))}
        </div>

        <Button
          onClick={onEnter}
          data-testid="briefing-enter"
          className="mt-4 h-14 rounded-full bg-[#c7a25a] px-10 text-[16px] font-medium text-[#0d1310] shadow-[0_24px_80px_-20px_rgba(199,162,90,0.6)] hover:bg-[#d4b06e]"
        >
          Enter my workspace →
        </Button>
      </div>
    </div>
  );
}
